import React from 'react';
import type { LinkProps } from './interface';
import { useNavigateContext } from '@src/contexts/NavigateContext';
import MuiLink from '@mui/material/Link';
import { Link as ReactRouterLink } from 'react-router-dom';
import { getTestId } from '@src/utils/testIds';
import { colorStringToPalette } from '@src/utils';

const BASE_STYLE: React.CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  letterSpacing: 'inherit',
};
const NO_UNDERLINE_STYLE: React.CSSProperties = {
  ...BASE_STYLE,
  textDecoration: 'none',
};

export function Link<RouteMap>(props: LinkProps<RouteMap>) {
  const { variant = 'text', children, url } = props;
  const { getRouteUrl } = useNavigateContext();
  const isExternal = !!url;
  const isTextLink = variant === 'text';

  const to = !isExternal
    ? // We cast as never to avoid a Typescript function overload annoyance
      // This would satisfy Typescript but serves no functional purpose (params ? getRouteUrl(to, params) : getRouteUrl(to))
      getRouteUrl(props.to as never, props.params as never)
    : // React router v5 requires us to use pathname for external urls
      { pathname: url };
  const color = isTextLink ? colorStringToPalette('state.teal.secondary') : undefined;
  const target = isExternal ? '_blank' : undefined;
  const rel = isExternal ? 'noopener noreferrer' : undefined;
  const style = isTextLink ? BASE_STYLE : NO_UNDERLINE_STYLE;

  return (
    <MuiLink
      component={ReactRouterLink}
      to={to}
      color={color}
      target={target}
      rel={rel}
      style={style}
      data-testid={getTestId(props)}
    >
      {children}
    </MuiLink>
  );
}
